import { CanceledError } from 'axios';
import { ApiError } from './api-error';
import type { ApiClient, ApiRequestConfig } from './client';

export interface DownloadFile {
  readonly blob: Blob;
  readonly fileName: string;
  readonly status: number;
}

/** Nome do Content-Disposition; filename* UTF-8 tem precedência sobre filename. */
export function downloadFileName(disposition: string | undefined, fallback: string): string {
  const extended = /(?:^|;)\s*filename\*\s*=\s*(?:UTF-8)?'[^']*'([^;]+)/i.exec(disposition ?? '');
  const regular = /(?:^|;)\s*filename\s*=\s*(?:"((?:[^"\\]|\\.)*)"|([^;]+))/i.exec(
    disposition ?? '',
  );
  let name = extended?.[1]?.trim() || regular?.[1]?.replace(/\\(.)/g, '$1') || regular?.[2]?.trim();
  if (name) {
    try {
      name = decodeURIComponent(name);
    } catch {
      /* U02: conserva nome com escape inválido. */
    }
  }
  return cleanFileName(name, fallback);
}

/** Só o último segmento, sem caractere de controle. */
function cleanFileName(name: string | undefined, fallback: string): string {
  return (
    name
      ?.split(/[\\/]/)
      .at(-1)
      ?.replace(/\p{Cc}/gu, '')
      .trim() || fallback
  );
}

function checkCanceled(signal?: ApiRequestConfig['signal']) {
  if (signal?.aborted) throw ApiError.from(new CanceledError());
}

export interface RequestBlobOptions {
  readonly fallbackFileName: string;
  /**
   * Aceita também o relatório em base64 (`{ reportBase64, filename? }` em JSON, ou o texto base64 puro), como o
   * `downloadFileFromResponse` do legado (`utils/downloadFile.ts:20-36`, nome em `:87-89`). Binário segue valendo.
   */
  readonly acceptReportBase64?: boolean;
}

const OCTET_STREAM = 'application/octet-stream';

function decodeBase64(value: string): Uint8Array<ArrayBuffer> {
  const binary = atob(value.trim());
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
}

/** O relatório em base64 de uma resposta JSON ou texto, ou `undefined` quando a resposta é o próprio arquivo. */
async function reportFromText(blob: Blob, mime: string): Promise<{ readonly bytes: Uint8Array<ArrayBuffer>; readonly fileName?: string } | undefined> {
  const json = /[/+]json\b/i.test(mime);
  if (!json && !/^text\/plain\b/i.test(mime)) return undefined;
  const text = await blob.text();
  if (!json) return { bytes: decodeBase64(text) };
  const body: unknown = JSON.parse(text);
  if (typeof body === 'string') return { bytes: decodeBase64(body) };
  if (body === null || typeof body !== 'object') return undefined;
  const record = body as { readonly reportBase64?: unknown; readonly filename?: unknown };
  if (typeof record.reportBase64 !== 'string') return undefined;
  return {
    bytes: decodeBase64(record.reportBase64),
    ...(typeof record.filename === 'string' && record.filename !== '' ? { fileName: record.filename } : {}),
  };
}

/** Busca binário pelo cliente existente, preservando headers, timeout, query e cancelamento. */
export async function requestBlob(
  client: ApiClient,
  config: ApiRequestConfig,
  options: RequestBlobOptions,
): Promise<DownloadFile> {
  checkCanceled(config.signal);
  const response = await client.request<Blob>({ ...config, responseType: 'blob' });
  checkCanceled(config.signal);
  const mime = String(
    response.headers['content-type'] || response.data.type || OCTET_STREAM,
  );
  const blob =
    response.data instanceof Blob && response.data.type === mime
      ? response.data
      : new Blob([response.data], { type: mime });
  const disposition = response.headers['content-disposition'] as string | undefined;
  const report = options.acceptReportBase64 ? await reportFromText(blob, mime) : undefined;
  checkCanceled(config.signal);
  if (report) {
    // O legado prefere o nome do Content-Disposition e só então o `filename` do corpo.
    const named = disposition ? downloadFileName(disposition, '') : '';
    return {
      blob: new Blob([report.bytes], { type: OCTET_STREAM }),
      fileName: named || cleanFileName(report.fileName, options.fallbackFileName),
      status: response.status,
    };
  }
  return {
    blob,
    fileName: downloadFileName(disposition, options.fallbackFileName),
    status: response.status,
  };
}

/** Inicia o download no navegador e libera a URL temporária após o início (inclusive WebKit). */
export function downloadBlob(
  blob: Blob,
  fileName: string,
  options: { signal?: ApiRequestConfig['signal'] } = {},
): void {
  checkCanceled(options.signal);
  const href = URL.createObjectURL(blob);
  const link = document.createElement('a');
  try {
    link.href = href;
    link.download = fileName;
    document.body.appendChild(link);
    checkCanceled(options.signal);
    link.click();
  } finally {
    link.remove();
    setTimeout(() => URL.revokeObjectURL(href), 5000);
  }
}

/** Consulta e baixa um arquivo; feedback e estado em andamento continuam sob controle da ação read. */
export async function downloadFile(
  client: ApiClient,
  config: ApiRequestConfig,
  options: RequestBlobOptions,
): Promise<DownloadFile> {
  const file = await requestBlob(client, config, options);
  downloadBlob(file.blob, file.fileName, { signal: config.signal });
  return file;
}
